import React from "react";

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  ExternalLink,
  Lightbulb,
  type LucideIcon,
} from "lucide-react";

import { FLOW } from "./flowCopy";
import type {
  AnswerSeverity,
  AnswerSourceFlag,
  BriefingAnswer,
  BriefingCoverage,
} from "../../Types/AccountPlan";

/** Mock v4 answerCoverageClass: missing-from-deck → missing; a gap callout → partial. */
export const answerCoverage = (
  a: BriefingAnswer
): "answered" | "partial" | "missing" =>
  a.source_flag === "missing-from-deck"
    ? "missing"
    : a.gap_callout
      ? "partial"
      : "answered";

const CHIP =
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold";

export const COV_CLASS = {
  answered: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-800",
  missing: "bg-red-50 text-red-800",
  info: "bg-purple-50 text-[#8538d4]",
  neutral: "bg-slate-100 text-slate-600",
};

/** "2 answered · 2 partly answered · 1 missing from the plan" chips (BQ-10 legend). */
export const CoverageChips: React.FC<{ coverage: BriefingCoverage }> = ({
  coverage,
}) => (
  <>
    <span className={`${CHIP} ${COV_CLASS.answered}`}>
      {coverage.answered} answered
    </span>
    <span className={`${CHIP} ${COV_CLASS.partial}`}>
      {coverage.partial} partly answered
    </span>
    <span className={`${CHIP} ${COV_CLASS.missing}`}>
      {coverage.missing} missing from the plan
    </span>
  </>
);

export const SEVERITY: Record<
  AnswerSeverity,
  { text: string; plural: string; icon: LucideIcon; cls: string }
> = {
  "top-risk": {
    text: "Top risk",
    plural: "top risks",
    icon: AlertTriangle,
    cls: COV_CLASS.missing,
  },
  concern: {
    text: "Concern",
    plural: "concerns",
    icon: AlertCircle,
    cls: COV_CLASS.partial,
  },
  recommendation: {
    text: "Recommendation",
    plural: "recommendations",
    icon: Lightbulb,
    cls: COV_CLASS.info,
  },
  confirmed: {
    text: "Confirmed",
    plural: "confirmed",
    icon: CheckCircle2,
    cls: COV_CLASS.answered,
  },
  absent: {
    text: "Absent",
    plural: "absent",
    icon: CircleDashed,
    cls: COV_CLASS.neutral,
  },
};

export const SEVERITY_ORDER: AnswerSeverity[] = [
  "top-risk",
  "concern",
  "recommendation",
  "confirmed",
  "absent",
];

export const SeverityChip: React.FC<{
  severity: AnswerSeverity;
  count?: number;
}> = ({ severity, count }) => {
  const s = SEVERITY[severity];
  if (count !== undefined) {
    return (
      <span className={`${CHIP} ${s.cls}`}>
        {count} {count === 1 ? s.text.toLowerCase() : s.plural}
      </span>
    );
  }
  return (
    <span className={`${CHIP} ${s.cls}`}>
      <s.icon className="h-3 w-3" aria-hidden />
      {s.text}
    </span>
  );
};

const FLAG_CLASS: Record<AnswerSourceFlag, string> = {
  "explicit-stated": "bg-green-100 text-green-800",
  "implicit-inferred": "bg-amber-100 text-amber-800",
  "missing-from-deck": "bg-red-100 text-red-800",
};

/** Where an answer came from, in plain words (UI tour item 10: never the raw enum). */
export const SOURCE_FLAG_LABEL: Record<AnswerSourceFlag, string> = {
  "explicit-stated": "Stated in the plan",
  "implicit-inferred": "Inferred from the plan",
  "missing-from-deck": "Not in the plan",
};

export const SourceFlag: React.FC<{ flag: AnswerSourceFlag }> = ({ flag }) =>
  SOURCE_FLAG_LABEL[flag] ? (
    <span
      data-testid="source-flag"
      className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${FLAG_CLASS[flag]}`}
    >
      {SOURCE_FLAG_LABEL[flag]}
    </span>
  ) : null;

/** Mock v4 colors the direct answer by its verdict. */
export const VERDICT_CLASS = {
  yes: "bg-emerald-100 text-emerald-800",
  no: "bg-rose-100 text-rose-800",
  partial: "bg-amber-100 text-amber-800",
};

/** Mock v4 citation chip: opens the slide in View source. */
export const SlideChip: React.FC<{
  slide: number;
  onOpen: (slide: number) => void;
}> = ({ slide, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(slide)}
    title={`Open slide ${slide} in ${FLOW.viewSource}`}
    className="inline-flex shrink-0 items-center gap-1 rounded bg-purple-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold not-italic text-[#8538d4] transition hover:-translate-y-px hover:bg-[#974ae4] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
  >
    <ExternalLink className="h-2.5 w-2.5" aria-hidden />
    slide {slide}
  </button>
);
